/**
 * Resolve front-end asset paths in JS. These are thin mirrors of the PHP
 * helpers `asset_url()` / `cdn_url()` — **the PHP helpers are the single source
 * of truth for CDN handling.** The master layout injects their resolved bases
 * once as `window.assetBase = asset_url('')` and `window.cdnBase = cdn_url('')`;
 * the JS only concatenates a path onto whatever base the helper produced.
 *
 * Never hardcode a CDN host here. Because the bases come from the PHP helpers,
 * the local-vs-prod decision lives in one place: in production they resolve to
 * the CDN, and in local/dev they fall back to the app origin (the helpers return
 * `url('')` when not in production) — so dev always loads your **local** images,
 * never `cdn.patchbot.io`.
 *
 *   assetUrl(path) → mirrors asset_url(): versioned per-build dir in prod. For
 *     normal front-end assets that ship with each build.
 *
 *   cdnUrl(path)   → mirrors cdn_url(): stable bucket root in prod, never
 *     build-versioned. For large / long-lived media that must stay aggressively
 *     cached across deploys.
 *
 * `path` keeps its leading slash; the bases have no trailing slash. Each falls
 * back to the bare path when its base is absent (rendered outside the layout).
 */
let serverBases = {};

/*
 * The SSR renderer has no window, so it receives the same bases as Inertia props.
 * They only change between deploys, and every deploy restarts the renderer, so
 * holding them in module state across concurrent renders is safe.
 */
export const setServerAssetBases = ({ assetBase, cdnBase }) => {
    serverBases = { assetBase, cdnBase };
};

const bases = () => (typeof window === "undefined" ? serverBases : window);

export const assetUrl = (path) => `${bases().assetBase ?? ""}${path}`;

export const cdnUrl = (path) => `${bases().cdnBase ?? ""}${path}`;
